/**
 * `score(scheme, responses)` — the only code in the system that turns a response
 * into a score.
 *
 * It runs unchanged in two places: in the browser during self-learning, so
 * feedback is instant, and on the server for the record. That is what makes the
 * two agree *by construction* rather than by discipline, and it is why this
 * package has no dependencies and touches no clock, no random source and no
 * locale-sensitive comparison.
 *
 * What it is not: a service, a store, or anything that knows who a learner is.
 * `{ scheme, responses }` in, a score out.
 */

import { ITEM_TYPES } from '@cet/asset-contract';
import type {
  AggregateDoc,
  ItemResponse,
  ItemSchemeDoc,
  ItemVerdict,
  Judgement,
  Locale,
  Score,
  ScoreResult,
  SchemeDoc,
} from '@cet/asset-contract';

import { ScoringError } from './errors.js';
import { scoreChoice } from './items/choice.js';
import { scoreMatching } from './items/matching.js';
import { scoreMultiChoice } from './items/multi-choice.js';
import { scoreNumeric } from './items/numeric.js';
import { scoreOrdering } from './items/ordering.js';
import { scoreTextEntry } from './items/text-entry.js';
import { SCORING_VERSION } from './version.js';

export interface ScoreInput {
  readonly scheme: SchemeDoc;
  /**
   * Keyed by item id. An item absent from this map, or present as `null`, is
   * unanswered — which earns no points and is reported as `answered: false`.
   */
  readonly responses: Readonly<Record<string, ItemResponse | null | undefined>>;
  /** Used only to resolve feedback text. It cannot affect whether an answer is correct. */
  readonly locale: Locale;
  /**
   * The AI's judgements of open answers, by item (D93) — made host side and handed in as a seed
   * is, so this stays pure. A judged item's answer with none is `correct: null`: not judged yet.
   */
  readonly judgements?: Readonly<Record<string, Judgement>>;
}

export function score(input: ScoreInput): ScoreResult {
  const { scheme, responses, locale, judgements } = input;

  // A response for an item the scheme does not know is a version mismatch
  // between what the learner saw and what is grading them. Loud, because the
  // alternative is silently dropping a real answer.
  const unknown = Object.keys(responses).filter((itemId) => scheme.items[itemId] === undefined);
  if (unknown.length > 0) {
    throw new ScoringError(
      'unknown-item',
      `Responses were supplied for items that this scheme does not grade: ${unknown.join(', ')}. ` +
        `The scheme is for ${scheme.asset}@${scheme.version}; check that the host pinned the ` +
        `version the learner was shown.`,
      `responses.${unknown[0]}`,
    );
  }

  // Sorted, so the order of `perItem` cannot depend on how the scheme's JSON
  // happened to be written. The browser and the server have to agree byte for
  // byte, and object key order is not a guarantee worth resting that on.
  const itemIds = Object.keys(scheme.items).sort();

  const perItem = itemIds.map((itemId) =>
    scoreItem(itemId, scheme.items[itemId]!, responses[itemId], locale, judgements?.[itemId]),
  );

  const parts = scheme.aggregate?.parts;
  return {
    total: aggregate(perItem, scheme.aggregate),
    // Each part summed alone, keys sorted as the items are; a part's `passing` is the host's (D93).
    ...(parts === undefined
      ? {}
      : {
          parts: Object.fromEntries(
            Object.keys(parts).sort().map((partId) => {
              const members = new Set(parts[partId]);
              return [partId, aggregate(perItem.filter((verdict) => members.has(verdict.itemId)), undefined)];
            }),
          ),
        }),
    perItem,
    scoringVersion: SCORING_VERSION,
  };
}

/**
 * One item. Exported because the SDK grades a single item at a time in practice
 * mode — the learner answers one question and wants an answer about that
 * question, not a recomputed total.
 */
export function scoreItem(
  itemId: string,
  scheme: ItemSchemeDoc,
  response: ItemResponse | null | undefined,
  locale: Locale,
  /** For a judged `textEntry` (D93): the AI's judgement of this answer, if there is one. */
  judgement?: Judgement,
): ItemVerdict {
  switch (scheme.type) {
    case 'choice':
      return scoreChoice(itemId, scheme, response, locale);
    case 'multiChoice':
      return scoreMultiChoice(itemId, scheme, response, locale);
    case 'textEntry':
      return scoreTextEntry(itemId, scheme, response, locale, judgement);
    case 'ordering':
      return scoreOrdering(itemId, scheme, response, locale);
    case 'matching':
      return scoreMatching(itemId, scheme, response, locale);
    case 'numeric':
      return scoreNumeric(itemId, scheme, response, locale);
    default:
      /**
       * A type this scorer has no case for.
       *
       * TypeScript believes the switch above is exhaustive, because `ItemSchemeDoc` is a closed
       * union — and it is, for anything the compiler saw. A scheme arrives as JSON from a host,
       * so at runtime `type` can be any string at all: an asset built against a newer contract,
       * a hand-edited file, a typo.
       *
       * Without this branch the function returned `undefined`, and the caller a few lines up
       * added `verdict.points` to a total — so a mistyped item type surfaced as
       * `Cannot read properties of undefined`, from a stack frame that mentions neither the
       * item nor the type. Reported by another team, who hit it on their first run.
       */
      throw new ScoringError(
        'unknown-item-type',
        `Item "${itemId}" declares type "${String((scheme as { type: unknown }).type)}", which ` +
          `this scorer version does not grade. Known types: ${ITEM_TYPES.join(', ')}. Check the ` +
          `scoring version the attempt was pinned to — a scheme written against a newer contract ` +
          `cannot be graded by an older scorer.`,
        `items.${itemId}.type`,
      );
  }
}

/**
 * Sum, which is the only method Phase 1 implements and the default when no
 * `aggregate` block is present.
 *
 * `raw` and `max` accumulate as integers. `scaled` is derived once on the way out
 * for a host that needs a fraction — xAPI does — and nothing compares against it.
 * `passed` uses `raw * 100 >= passing * max` rather than `scaled >= passing / 100`
 * so that no rounding can decide a pass. Not because a specific case diverges
 * today, but because this form cannot.
 */
function aggregate(perItem: readonly ItemVerdict[], spec: AggregateDoc | undefined): Score {
  let raw = 0;
  let max = 0;
  for (const verdict of perItem) {
    raw += verdict.points;
    max += verdict.max;
  }

  const scaled = max === 0 ? 0 : raw / max;
  const passing = spec?.passing;

  if (passing === undefined) return { raw, max, scaled };
  return { raw, max, scaled, passed: raw * 100 >= passing * max };
}
